import { useEffect, useRef, useState } from "react";
import { ApiError } from "@workspace/api-client-react";
import { toast } from "@/hooks/use-toast";
import { registerRouteCooldown, wasRecentUserInteraction } from "@/lib/throttle-toast";

export type RateLimitInfo = {
  retryAfterSec: number;
};

function readRetryAfterFromHeaders(headers: Headers | undefined): number | null {
  if (!headers) return null;
  const raw = headers.get("retry-after");
  if (!raw) return null;
  const n = Number.parseInt(raw, 10);
  if (Number.isFinite(n) && n > 0) return n;
  return null;
}

function readRetryAfterFromBody(data: unknown): number | null {
  if (!data || typeof data !== "object") return null;
  const v = (data as { retryAfterSec?: unknown }).retryAfterSec;
  if (typeof v === "number" && Number.isFinite(v) && v > 0) return Math.ceil(v);
  return null;
}

/**
 * Detect a per-user throttle response (HTTP 429) from one of the heavy
 * report endpoints. Returns the suggested cooldown in seconds (server
 * sends both a `Retry-After` header and a `retryAfterSec` field on the
 * JSON body — we trust the body first, header second, and clamp to a
 * sensible floor).
 */
export function parseRateLimit(error: unknown): RateLimitInfo | null {
  if (!(error instanceof ApiError)) return null;
  if (error.status !== 429) return null;
  const fromBody = readRetryAfterFromBody(error.data);
  const fromHeader = readRetryAfterFromHeaders(error.headers);
  const retryAfterSec = Math.max(1, fromBody ?? fromHeader ?? 5);
  return { retryAfterSec };
}

type ShowOpts = {
  title?: string;
  label?: string;
  autoRefresh?: boolean;
};

const TOAST_DEDUPE_WINDOW_MS = 3_000;
const recentToastAt = new Map<string, number>();

export function showRateLimitToast(
  info: RateLimitInfo,
  opts: ShowOpts = {},
): void {
  const key = `${opts.label ?? "default"}:${info.retryAfterSec}`;
  const now = Date.now();
  const last = recentToastAt.get(key) ?? 0;
  if (now - last < TOAST_DEDUPE_WINDOW_MS) return;
  recentToastAt.set(key, now);

  const subject = opts.label ?? "This report";
  const tail = opts.autoRefresh
    ? "The next refresh will run automatically."
    : "Try again in a moment.";
  toast({
    title: opts.title ?? `Too busy — try again in ${info.retryAfterSec}s`,
    description: `${subject} is rate-limited to protect the database. ${tail}`,
    variant: "default",
  });
}

/**
 * React Query helpers for the 429 throttle:
 *
 *  - Call this BEFORE the `useQuery`-style hook to read its current
 *    cooldown state (because `refetchInterval` and `retry` must be
 *    supplied at query construction time, but the 429 we react to
 *    only surfaces afterwards via the query's `error`).
 *  - After the query, pass its `error` to `guard.handleError(error)`
 *    inside the same render. The guard shows a friendly toast on the
 *    first 429 (deduped across rapid duplicate errors) and starts a
 *    cooldown window equal to the server-suggested `retryAfterSec`.
 *  - While in cooldown the returned `refetchInterval` is `false`, so
 *    the 60s auto-refresh stops re-firing immediately. Suppresses
 *    React Query's exponential-backoff retry for 429 too.
 *  - Exposes `cooldownSec` so callers can countdown a manual refresh
 *    button.
 */
export function useRateLimitGuard(opts: {
  label: string;
  baseInterval?: number | false;
}) {
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [, setTick] = useState(0);
  const lastHandledRef = useRef<unknown>(null);

  // Re-render once the cooldown elapses so refetchInterval flips back
  // on. Polls at 1s while active for the visible countdown.
  useEffect(() => {
    if (cooldownUntil <= Date.now()) return;
    const remaining = cooldownUntil - Date.now();
    const t = setTimeout(
      () => setTick((x) => x + 1),
      Math.min(Math.max(remaining + 50, 100), 1_000),
    );
    return () => clearTimeout(t);
  });

  const remainingMs = cooldownUntil - Date.now();
  const inCooldown = remainingMs > 0;
  const cooldownSec = inCooldown ? Math.ceil(remainingMs / 1000) : 0;
  const baseInterval = opts.baseInterval ?? false;

  // Effect-driven error handler so we never schedule a setState during
  // the caller's render. Callers pass the live `error` from useQuery
  // via `guard.useHandleError(error)` and we react on change.
  function useHandleError(error: unknown): void {
    useEffect(() => {
      if (error === lastHandledRef.current) return;
      lastHandledRef.current = error;
      const info = parseRateLimit(error);
      if (!info) return;
      const autoRefresh =
        typeof opts.baseInterval === "number" && opts.baseInterval > 0;
      // Share the cooldown with the route-level fetch gate (Task #1917) so
      // sibling widgets on the same route stop fetching too.
      if (error instanceof ApiError && error.url) {
        registerRouteCooldown(error.url, info.retryAfterSec);
      }
      // Background auto-refresh throttles back off silently — only toast
      // when the operator actually did something (Task #1917).
      if (!autoRefresh || wasRecentUserInteraction()) {
        showRateLimitToast(info, { label: opts.label, autoRefresh });
      }
      setCooldownUntil(Date.now() + info.retryAfterSec * 1000);
    }, [error]);
  }

  return {
    inCooldown,
    cooldownSec,
    refetchInterval: inCooldown ? (false as const) : baseInterval,
    retry: (failureCount: number, err: unknown) => {
      if (err instanceof ApiError && err.status === 429) return false;
      return failureCount < 3;
    },
    useHandleError,
  };
}

/** Returns true when this error is a 429 rate-limit response. */
export function isRateLimitError(error: unknown): boolean {
  return parseRateLimit(error) !== null;
}
